<template>
  <section class="ncrm-privacy-contact-points">
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-3">
      <div>
        <h3 class="text-subtitle-1">{{ $t(`${copyKey}.title`) }}</h3>
        <p class="text-body-2 text-medium-emphasis mb-0">{{ $t(`${copyKey}.hint`) }}</p>
      </div>
      <!-- Opoziția este o declarație primită, nu configurare de politică: o pot
           consemna aceleași roluri care pot înregistra dovadă manuală. -->
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-hand-back-left-outline"
        @click="$emit('record-objection', $event)"
      >
        {{ $t(`${copyKey}.objection.open`) }}
      </v-btn>
    </div>

    <v-skeleton-loader v-if="loading" type="table-row-divider@2" />
    <v-alert v-else-if="errorMessage" type="error">
      {{ errorMessage }}
      <template #append>
        <v-btn variant="text" @click="fetchItems">{{ $t(`${copyKey}.retry`) }}</v-btn>
      </template>
    </v-alert>
    <v-alert v-else-if="!items.length" type="info">
      {{ $t(`${copyKey}.empty`) }}
    </v-alert>
    <div v-else class="ncrm-privacy-points">
      <div class="ncrm-privacy-points__header" aria-hidden="true">
        <span>{{ $t(`${copyKey}.columns.value`) }}</span>
        <span>{{ $t(`${copyKey}.columns.slot`) }}</span>
        <span>{{ $t(`${copyKey}.columns.classification`) }}</span>
        <span>{{ $t(`${copyKey}.columns.person`) }}</span>
        <span>{{ $t(`${copyKey}.columns.state`) }}</span>
        <span v-if="canManage" />
      </div>
      <div v-for="item in items" :key="item.link_id" class="ncrm-privacy-points__row">
        <span :data-label="$t(`${copyKey}.columns.value`)">
          <v-icon size="16" class="mr-1 text-medium-emphasis">
            {{ item.type === "email" ? "mdi-email-outline" : "mdi-phone-outline" }}
          </v-icon>
          {{ item.value_display }}
          <v-chip v-if="item.is_primary" size="x-small" class="ml-1">
            {{ $t(`${copyKey}.primary`) }}
          </v-chip>
        </span>
        <span :data-label="$t(`${copyKey}.columns.slot`)">{{ slotLabel(item.slot) }}</span>
        <span :data-label="$t(`${copyKey}.columns.classification`)">
          {{ $t(`ncrm.contacts.dataProtection.record.contactPoints.classifications.${item.classification}`) }}
        </span>
        <span :data-label="$t(`${copyKey}.columns.person`)">
          <template v-if="item.linked_person">{{ personName(item.linked_person) }}</template>
          <template v-else-if="item.classification === 'linked_to_person'">
            <span class="text-medium-emphasis">{{ $t(`${copyKey}.personMissing`) }}</span>
          </template>
          <template v-else>{{ emptyLabel }}</template>
        </span>
        <span :data-label="$t(`${copyKey}.columns.state`)">
          <v-chip :color="stateStatus(item.state).color" size="x-small">
            <v-icon start size="12">{{ stateStatus(item.state).icon }}</v-icon>
            {{ stateLabel(item.state) }}
          </v-chip>
        </span>
        <span v-if="canManage" class="text-right">
          <v-btn
            size="small"
            variant="text"
            prepend-icon="mdi-tag-outline"
            @click="openClassification(item, $event)"
          >
            {{ $t(`${copyKey}.classify.open`) }}
          </v-btn>
        </span>
      </div>
    </div>

    <NcrmPrivacyContactPointClassificationDrawer
      v-if="canManage"
      v-model="classificationOpen"
      :company-id="companyId"
      :link="selectedLink"
      @saved="handleClassified"
    />
  </section>
</template>

<script>
import axios from "axios";
import NcrmPrivacyContactPointClassificationDrawer
  from "@/components/ncrm/shared/NcrmPrivacyContactPointClassificationDrawer.vue";
import { resolvePrivacyStatus } from "@/Utile/ncrmPrivacyStatus.mjs";

// Aceeași traducere stare punct → status vizual ca în matricea recordului, ca
// secțiunea și matricea să nu spună lucruri diferite despre același punct.
const STATE_SUMMARY = Object.freeze({
  active: "active",
  partial: "partial",
  pending: "pending",
  withdrawn_or_refused: "withdrawn_or_refused",
  undocumented: "undocumented",
  granted: "active",
  pending_confirmation: "pending",
  other_basis: "other_basis",
  refused: "withdrawn_or_refused",
  withdrawn: "withdrawn_or_refused",
  objected: "withdrawn_or_refused",
  restricted: "withdrawn_or_refused",
  expired: "partial",
});

export default {
  name: "NcrmPrivacyCompanyContactPoints",
  components: { NcrmPrivacyContactPointClassificationDrawer },
  props: {
    companyId: { type: [String, Number], required: true },
    canManage: { type: Boolean, default: false },
  },
  emits: ["record-objection", "changed"],
  data: () => ({
    loading: false,
    errorMessage: "",
    items: [],
    classificationOpen: false,
    selectedLink: null,
    lastTrigger: null,
    requestSequence: 0,
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.record.contactPoints"; },
    emptyLabel() { return this.$t("ncrm.contacts.details.emptyValue"); },
  },
  watch: {
    companyId: {
      immediate: true,
      handler() {
        this.classificationOpen = false;
        this.selectedLink = null;
        this.fetchItems();
      },
    },
    classificationOpen(value) { if (!value) this.$nextTick(this.restoreFocus); },
  },
  methods: {
    async fetchItems() {
      if (!this.companyId) return;
      const sequence = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get(`/ncrm/privacy/subjects/company/${this.companyId}/contact-points`);
        if (sequence !== this.requestSequence) return;
        this.items = Array.isArray(data?.items) ? data.items : [];
      } catch {
        if (sequence !== this.requestSequence) return;
        this.items = [];
        this.errorMessage = this.$t(`${this.copyKey}.loadError`);
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    slotLabel(slot) {
      if (!slot) return this.emptyLabel;
      const key = `${this.copyKey}.slots.${slot}`;
      if (typeof this.$te === "function" && !this.$te(key)) return slot.replaceAll("_", " ");
      return this.$t(key);
    },
    personName(person) {
      return [person.first_name, person.last_name].filter(Boolean).join(" ")
        || this.$t(`${this.copyKey}.personFallback`, { id: person.id });
    },
    stateStatus(state) { return resolvePrivacyStatus(STATE_SUMMARY[state] || "undocumented"); },
    stateLabel(state) {
      return this.$t(this.stateStatus(state).labelKey);
    },
    openClassification(item, event) {
      this.lastTrigger = event?.currentTarget || null;
      this.selectedLink = item;
      this.classificationOpen = true;
    },
    handleClassified({ companyId } = {}) {
      if (String(companyId) !== String(this.companyId)) return;
      this.fetchItems();
      this.$emit("changed");
    },
    restoreFocus() {
      this.lastTrigger?.focus?.();
      this.lastTrigger = null;
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-points { border: 1px solid rgba(var(--v-theme-on-surface), 0.08); border-radius: var(--app-radius-base); overflow: hidden; }
.ncrm-privacy-points__header, .ncrm-privacy-points__row { display: grid; grid-template-columns: 1.4fr .9fr 1.1fr 1fr .9fr auto; gap: 12px; align-items: center; padding: 10px 12px; }
.ncrm-privacy-points__header { background: rgba(var(--v-theme-on-surface), 0.04); font-size: .75rem; font-weight: 600; text-transform: uppercase; }
.ncrm-privacy-points__row { border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); }
@media (max-width: 860px) {
  .ncrm-privacy-points__header { display: none; }
  .ncrm-privacy-points__row { grid-template-columns: 1fr; gap: 6px; padding: 14px; }
  /* Numai celulele cu etichetă primesc prefixul; celula de acțiune nu are
     `data-label`, iar regula generică îi lipea un `:` orfan. */
  .ncrm-privacy-points__row > span[data-label]::before { content: attr(data-label) ": "; font-size: .75rem; opacity: .68; }
  .ncrm-privacy-points__row > span.text-right { text-align: left !important; }
}
</style>
